import { BaseProvider } from "./base.provider";

export interface EventTierPlan {
  tier_id: string;
  tier_number: number;
  tier_name: string;
  price_amount: number;
  price_currency: string;
  minimum_boost: number;
  has_player_checkin: boolean;
  has_badge: boolean;
  has_route_builder: boolean;
  is_visible_on_map: boolean;
  is_discoverable: boolean;
  is_advertisable: boolean;
  is_featured: boolean;
  has_analytics: boolean;
  points_multiplier: number;
  primary_color_hex_code: string;
  secondary_color_hex_code: string;
  is_enabled: boolean;
  created_at: string;
}

export interface CreateTierPayload {
  tier_number: number;
  tier_name: string;
  price_amount?: number;
  price_currency: string;
  minimum_boost?: number;
  has_player_checkin?: boolean;
  has_badge?: boolean;
  has_route_builder?: boolean;
  is_visible_on_map?: boolean;
  is_discoverable?: boolean;
  is_advertisable?: boolean;
  is_featured?: boolean;
  has_analytics?: boolean;
  points_multiplier?: number;
  primary_color_hex_code: string;
  secondary_color_hex_code: string;
  is_enabled?: boolean;
}

export interface Event {
  event_id: string;
  created_by: string;
  created_by_username: string;
  club_id: string | null;
  club_name: string | null;
  tier_id: string;
  tier_name: string;
  tier_primary_color_hex_code: string;
  tier_secondary_color_hex_code: string;
  title: string;
  description: string | null;
  status: "draft" | "published" | "cancelled" | "completed";
  starts_at: string;
  ends_at: string | null;
  location_lng: number | null;
  location_lat: number | null;
  location_label: string | null;
  banner_obj_url: string | null;
  boost_score: number;
  score?: number;
  created_at: string;
  updated_at: string;
}

export interface EventParticipant {
  user_id: string;
  username: string;
  joined_at: string;
  vehicle_id: string | null;
  vehicle_photo_url: string | null;
  vehicle_nickname: string | null;
  vehicle_make: string | null;
  vehicle_model: string | null;
  vehicle_type: "car" | "motorcycle" | null;
  vehicle_horsepower: number | null;
  vehicle_drive: "FWD" | "RWD" | "AWD" | null;
  vehicle_transmission: "A" | "M" | null;
  vehicle_engine_position: "front" | "center" | "rear" | null;
}

export interface EventBoost {
  boost_id: string;
  user_id: string;
  points_spent: number;
  created_at: string;
}

export interface EventBadge {
  badge_id: string;
  name: string;
  img_url: string;
  variant_name: "standard" | "shiny";
  filter_img_url: string | null;
  is_visible: boolean;
  available_from: string | null;
  available_to: string | null;
  action_type: string;
  action_threshold: number;
  action_description: string;
  enabled: boolean;
}

export interface PaginatedEvents {
  data: Event[];
  page: number;
  page_size: number;
  has_next: boolean;
}

export interface PaginatedParticipants {
  data: EventParticipant[];
  page: number;
  page_size: number;
  has_next: boolean;
}

export interface BannerUploadResponse {
  upload_url: string;
  form_fields: Record<string, string>;
}

export interface UpdateEventPayload {
  title: string;
  starts_at: string;
  description?: string | null;
  ends_at?: string | null;
  location_label?: string | null;
  location_lat?: number | null;
  location_lng?: number | null;
}

class EventsProvider extends BaseProvider {
  listTiers(): Promise<EventTierPlan[]> {
    return this.request<EventTierPlan[]>("/v1/events/tiers");
  }

  createTier(payload: CreateTierPayload): Promise<EventTierPlan> {
    return this.request<EventTierPlan>("/v1/admin/events/tiers", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  updateTier(
    tierId: string,
    payload: CreateTierPayload,
  ): Promise<EventTierPlan> {
    return this.request<EventTierPlan>(`/v1/admin/events/tiers/${tierId}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  }

  deleteTier(tierId: string): Promise<string> {
    return this.request<string>(`/v1/admin/events/tiers/${tierId}`, {
      method: "DELETE",
    });
  }

  getRecommended(
    params: {
      page?: number;
      lat?: number;
      lng?: number;
      w_timing?: number;
      w_distance?: number;
      w_club?: number;
      w_tier?: number;
      w_participants?: number;
      refresh?: boolean;
    } = {},
  ): Promise<PaginatedEvents> {
    const p = new URLSearchParams();
    if (params.page !== undefined) p.set("page", String(params.page));
    if (params.lat !== undefined) p.set("lat", String(params.lat));
    if (params.lng !== undefined) p.set("lng", String(params.lng));
    if (params.w_timing !== undefined)
      p.set("w_timing", String(params.w_timing));
    if (params.w_distance !== undefined)
      p.set("w_distance", String(params.w_distance));
    if (params.w_club !== undefined) p.set("w_club", String(params.w_club));
    if (params.w_tier !== undefined) p.set("w_tier", String(params.w_tier));
    if (params.w_participants !== undefined)
      p.set("w_participants", String(params.w_participants));
    if (params.refresh !== undefined) p.set("refresh", String(params.refresh));
    return this.request<PaginatedEvents>(`/v1/events/recommended?${p}`);
  }

  searchText(q: string, page: number = 0): Promise<PaginatedEvents> {
    const params = new URLSearchParams({ q, page: String(page) });
    return this.request<PaginatedEvents>(`/v1/events/search/text?${params}`);
  }

  adminList(page: number = 0, userId?: string): Promise<PaginatedEvents> {
    const params = new URLSearchParams({ page: String(page) });
    if (userId) params.set("user_id", userId);
    return this.request<PaginatedEvents>(`/v1/admin/events?${params}`);
  }

  adminUpdate(eventId: string, payload: UpdateEventPayload): Promise<string> {
    return this.request<string>(
      `/v1/admin/events/${encodeURIComponent(eventId)}`,
      {
        method: "PUT",
        body: JSON.stringify(payload),
      },
    );
  }

  adminDelete(eventId: string): Promise<string> {
    return this.request<string>(
      `/v1/admin/events/${encodeURIComponent(eventId)}`,
      {
        method: "DELETE",
      },
    );
  }

  getById(eventId: string): Promise<Event> {
    return this.request<Event>(`/v1/events/${encodeURIComponent(eventId)}`);
  }

  getBannerUploadUrl(eventId: string): Promise<BannerUploadResponse> {
    return this.request<BannerUploadResponse>(
      `/v1/events/${encodeURIComponent(eventId)}/banner`,
      { method: "POST" },
    );
  }

  async uploadBannerToStorage(
    { upload_url, form_fields }: BannerUploadResponse,
    file: File,
  ): Promise<void> {
    const form = new FormData();
    for (const [key, value] of Object.entries(form_fields)) {
      form.append(key, value);
    }
    form.append("Content-Type", file.type);
    form.append("file", file);
    const res = await fetch(upload_url, { method: "POST", body: form });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(text || `Storage upload failed: HTTP ${res.status}`);
    }
  }

  listParticipants(eventId: string, page = 1): Promise<PaginatedParticipants> {
    return this.request<PaginatedParticipants>(
      `/v1/events/${encodeURIComponent(eventId)}/participants?page=${page}`,
    );
  }

  listBoosts(eventId: string): Promise<EventBoost[]> {
    return this.request<EventBoost[]>(
      `/v1/admin/events/${encodeURIComponent(eventId)}/boosts`,
    );
  }

  listBadges(eventId: string): Promise<EventBadge[]> {
    return this.request<EventBadge[]>(
      `/v1/events/${encodeURIComponent(eventId)}/badges`,
    );
  }

  adminListBadges(eventId: string): Promise<EventBadge[]> {
    return this.request<EventBadge[]>(
      `/v1/admin/events/${encodeURIComponent(eventId)}/badges`,
    );
  }

  regenerateBadge(eventId: string): Promise<void> {
    return this.request<void>(
      `/v1/admin/events/${encodeURIComponent(eventId)}/badge/regenerate`,
      { method: "POST" },
    );
  }

  setBadgeEnabled(
    eventId: string,
    badgeId: string,
    enabled: boolean,
  ): Promise<void> {
    return this.request<void>(
      `/v1/admin/events/${encodeURIComponent(eventId)}/badges/${encodeURIComponent(badgeId)}`,
      {
        method: "PATCH",
        body: JSON.stringify({ enabled }),
      },
    );
  }
}

export const eventsProvider = new EventsProvider();
